import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowRight, FiAward, FiTrendingUp, FiUsers } from 'react-icons/fi';
import Masonry from 'react-masonry-css';
import anulogo from '../assets/images/logo-ANU-black.svg';
import trilogo from '../assets/images/TRI-logo.png';
import adclogo from '../assets/images/adc-logo.svg';
import aihwlogo from '../assets/images/aihw-logo.svg';
import tgalogo from '../assets/images/gta-logo.png';
import cathlogo from '../assets/images/cath-logo.png';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const Container = styled.section`
  padding: 8rem 0rem;
//   background: linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%);
  position: relative;
  overflow: hidden;
`;

const GlassCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.18);
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #2d3436 0%, #636e72 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
`;

const StyledMasonry = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 2rem;
  margin: 0 auto;
  padding: 0 2rem;

  & > *:nth-child(4) {
    grid-column: 4 / span 5;
    margin: 0 auto;
  }

  @media (max-width: 1200px) {
    & > *:nth-child(4) {
      grid-column: span 6;
      margin: 0;
    }
  }

  @media (max-width: 768px) {
    & > *:nth-child(4) {
      grid-column: span 12;
    }
  }
`;

const CaseStudyCard = styled(motion.div)`
  background: linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%);
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  
  grid-column: ${props => {
    switch(props.size) {
      case 'large':
        return 'span 6';
      case 'medium':
        return 'span 5';
      case 'small':
        return 'span 4';
      default:
        return 'span 4';
    }
  }};
  
  grid-row: ${props => props.size === 'large' ? 'span 2' : 'span 1'};
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 1200px) {
    grid-column: span 6;
  }

  @media (max-width: 768px) {
    grid-column: span 12;
  }
`;

const CompanyLogo = styled.div`
  width: ${props => props.size === 'large' ? '100px' : '80px'};
  height: ${props => props.size === 'large' ? '100px' : '80px'};
  border-radius: 16px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  animation: ${float} 6s ease-in-out infinite;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1) rotate(5deg);
  }

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
  }
`;

const CompanyName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #2d3436;

  a{
    color: #2d3436;
    text-decoration: none;
  }
`;

const ResultStats = styled.div`
  display: grid;
  grid-template-columns: ${props => 
    props.size === 'large' ? 'repeat(3, 1fr)' : 
    props.size === 'medium' ? 'repeat(2, 1fr)' : 
    'repeat(1, 1fr)'
  };
  gap: 1rem;
  margin: 1.5rem 0;
`;

const Stat = styled.div`
  text-align: center;
  padding: 1rem;
  background: ${props => `${props.color}10`};
  border-radius: 12px;

  h4 {
    font-size: 1.8rem;
    color: ${props => props.color};
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9rem;
    color: #636e72;
  }
`;

const ReadMore = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #0984e3;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  transition: gap 0.3s ease;

  &:hover {
    gap: 1rem;
  }
`;

const ClientSuccessSection = () => {
  const caseStudies = [
    {
      company: "Australian National University",
      logo: anulogo,
      color: "rgba(0, 0, 0, 0.05)",
      link: 'https://asiapacific.anu.edu.au/',
      size: "large",
      description: "Drupal CMS redevelopment: UI/UX, Headless, Automated workflows, and light-weight content management operations.",
      results: [
        { value: "70%+", label: "Expense Reduction on College Website Maintenance", color: "#FF6B6B" },
        { value: "2.7x", label: "Session Duration", color: "#4ECDC4" },
        { value: "1.4x", label: "New Users", color: "#4ECDC4" },
        { value: "1 Codebase", label: "30+ Departments & Centres", color: "#45B7D1" }
      ]
    },
    {
      company: "CAiTH",
      logo: cathlogo,
      color: "rgba(0, 0, 0, 0.05)",
      size: "medium",
      link: 'https://cath.live/',
      description: "Web3 Dapp, AI Agent, and smart contracts development, significant accessibility improvements for the disabilities with AI Agent.",
      results: [
        { value: "60%", label: "Faster Deploy", color: "#4ECDC4" },
        { value: "10%+", label: "Users are with disabilities", color: "#FF6B6B" },

      ]
    },
    {
      company: "Australian Institute of Health and Welfare",
      logo: aihwlogo,
      color: "rgba(0, 0, 0, 0.9)",
      size: "small",
      link: 'https://www.aihw.gov.au/',
      description: "Data processing, visualisation, and Kentico CMS redevelopment.",
      results: [
        { value: "70%", label: "Reduction in Data Management & Visualisation Complexity", color: "#FF6B6B" },
        { value: "30%", label: "Performance Improvement by headless architecture implementation", color: "#4ECDC4" }
      ]
    },
    {
      company: "Australian National University",
      logo: anulogo,
      color: "#A8E6CF",
      size: "medium",
      description: "Quantitative Trading Platform with complete new underlying architecture and trading engine development.",
      results: [
        { value: "20%", label: "Faster execution speed", color: "#FF6B6B" },
        { value: "12%", label: "Yield Improvement", color: "#4ECDC4" }
      ]
    }
  ];

  return (
    <Container>
      <Title>Client Success Stories</Title>
      <StyledMasonry>
        {caseStudies.map((study, index) => (
          <CaseStudyCard
            key={index}
            size={study.size}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: index * 0.2,
              duration: 0.5,
              ease: "easeOut"
            }}
          >
            <CompanyLogo color={study.color}>
              <img src={study.logo} alt={study.company} />
            </CompanyLogo>
            <CompanyName><a href={study.link} target="_blank" rel="noopener noreferrer">{study.company}</a></CompanyName>
            <p>{study.description}</p>
            <ResultStats size={study.size}>
              {study.results.map((result, i) => (
                <Stat 
                  key={i} 
                  color={result.color}
                  style={{
                    gridColumn: study.size === 'large' ? `span ${12 / study.results.length}` : 'span 1'
                  }}
                >
                  <h4>{result.value}</h4>
                  <p>{result.label}</p>
                </Stat>
              ))}
            </ResultStats>
            {/* <ReadMore>
              View Case Study <FiArrowRight />
            </ReadMore> */}
          </CaseStudyCard>
        ))}
      </StyledMasonry>
    </Container>
  );
};

export default ClientSuccessSection; 